import React from "react";

/**
 * Last-resort boundary around the route tree. Its main job is catching lazy
 * chunk loads that still fail after lazyWithRetry's single auto-reload (e.g.
 * a deploy happened mid-session) so the user gets a reload button instead of
 * a white screen.
 */
export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Boundary-caught errors never reach window.onerror, so report explicitly
    // or they vanish from Sentry. Dynamic import keeps @sentry/react out of
    // the entry chunk (it's lazy-initialized in main.jsx); the catch covers
    // the stale-deploy case where the sentry chunk itself fails to load.
    import("../libs/sentry")
      .then(({ sendExceptionToSentry }) =>
        sendExceptionToSentry({
          error,
          contextName: "react",
          context: { componentStack: errorInfo.componentStack },
          tags: { boundary: "AppErrorBoundary" },
        }),
      )
      .catch(() => {});
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          fontFamily: "sans-serif",
        }}
      >
        <div>Something went wrong loading Mattoboard.</div>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: "8px 24px", cursor: "pointer" }}
        >
          Reload
        </button>
      </div>
    );
  }
}
